import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Copy, Pencil, Trash2, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    'admin_client', 'comptable', 'controle_gestion', 'pilote_projet', 'daf',
    'directeur_projet', 'direction_generale', 'auditeur', 'financeur',
    'responsable_ong', 'gestionnaire_subventions', 'coordinateur_terrain', 'consultant_externe',
];

export default function Admin() {
    const { t } = useLang();
    const { user, session, profile } = useAuth();
    const { currentWorkspace, refresh: refreshWorkspaces, selectWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('pilote_projet');
    const [isAdmin, setIsAdmin] = useState(false);
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [pendingLink, setPendingLink] = useState(null);
    // Workspace edit/delete state
    const [editing, setEditing] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmName, setConfirmName] = useState('');

    const load = async () => {
        if (!currentWorkspace) return;
        const [{ data: m }, { data: inv }] = await Promise.all([
            supabase.from('workspace_members').select('user_id,role,is_admin,profiles:user_id(email,full_name)').eq('workspace_id', currentWorkspace.id),
            supabase.from('workspace_invitations').select('id,email,role,is_admin,token,accepted_at,created_at').eq('workspace_id', currentWorkspace.id).order('created_at', { ascending: false }),
        ]);
        setMembers(m || []); setInvitations(inv || []);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace, user]);

    const invite = async (e) => {
        e.preventDefault();
        setBusy(true); setFeedback(null); setPendingLink(null);
        try {
            const { data } = await axios.post(`${API}/invitations/send`,
                { workspace_id: currentWorkspace.id, email, role, is_admin: isAdmin },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            setFeedback({ type: 'ok', message: t('invitations.sent') });
            setEmail('');
            load();
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            // If SMTP not configured (503), the invitation row was still saved — fetch it & show link
            if (err.response?.status === 503) {
                // Fetch the latest invitation row for this email
                const { data: inv } = await supabase
                    .from('workspace_invitations')
                    .select('token')
                    .eq('workspace_id', currentWorkspace.id)
                    .eq('email', email)
                    .order('created_at', { ascending: false })
                    .limit(1).maybeSingle();
                if (inv?.token) {
                    const link = `${window.location.origin}/auth/invite/${inv.token}`;
                    setPendingLink(link);
                    setFeedback({ type: 'warn', message: t('invitations.smtp_missing') });
                    try {
                        await navigator.clipboard.writeText(link);
                    } catch (clipboardErr) {
                        // Clipboard API can fail in non-secure contexts or when permission is denied.
                        // Link is still shown to the user via setPendingLink so they can copy manually.
                        log.warn('[admin] clipboard write failed:', clipboardErr?.message);
                    }
                    setEmail('');
                    load();
                } else {
                    setFeedback({ type: 'error', message: detail });
                }
            } else {
                setFeedback({ type: 'error', message: detail });
            }
        }
        setBusy(false);
    };

    const copyLink = async (token) => {
        const link = `${window.location.origin}/auth/invite/${token}`;
        try {
            await navigator.clipboard.writeText(link);
        } catch (clipboardErr) {
            // Same caveat as above: keep the link visible in UI so the user can copy by hand.
            log.warn('[admin] clipboard write failed:', clipboardErr?.message);
        }
        setPendingLink(link);
        setFeedback({ type: 'ok', message: t('invitations.link_copy') });
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;

    const canManageWorkspace = currentWorkspace.is_admin || profile?.is_super_admin;

    const startEdit = () => { setNameDraft(currentWorkspace.name); setEditing(true); };
    const cancelEdit = () => { setEditing(false); setNameDraft(''); };

    const saveName = async () => {
        if (!nameDraft.trim() || nameDraft.trim().length < 2) { toast.error('Nom trop court (min 2 caractères)'); return; }
        if (nameDraft.trim() === currentWorkspace.name) { setEditing(false); return; }
        setSavingName(true);
        try {
            await axios.patch(`${API}/workspaces/${currentWorkspace.id}`,
                { name: nameDraft.trim() },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            toast.success('Nom de l\'espace mis à jour');
            setEditing(false);
            if (refreshWorkspaces) await refreshWorkspaces();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSavingName(false);
    };

    const normalize = (s) => (s || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
    const confirmDelete = async () => {
        if (normalize(confirmName) !== normalize(currentWorkspace.name)) { toast.error('Le nom de confirmation ne correspond pas'); return; }
        setBusy(true);
        try {
            await axios.delete(`${API}/workspaces/${currentWorkspace.id}`, {
                params: { confirm_name: normalize(currentWorkspace.name) },
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            toast.success('Espace supprimé');
            setDeleteOpen(false);
            if (refreshWorkspaces) await refreshWorkspaces();
            if (selectWorkspace) selectWorkspace(null);
            navigate('/app/dashboard');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    return (
        <div className="space-y-8">
            <div>
                <div className="eyebrow mb-2">{currentWorkspace.name}</div>
                <h1 className="font-heading text-4xl">{t('admin.title')}</h1>
            </div>

            {/* Workspace settings (rename + delete) */}
            <div className="card-soft p-6" data-testid="workspace-settings">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="eyebrow mb-2">Paramètres de l'espace</div>
                        {!editing ? (
                            <div className="flex items-center gap-3">
                                <div>
                                    <div className="font-heading text-2xl" data-testid="workspace-name-display">{currentWorkspace.name}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {currentWorkspace.industry || '—'} · {currentWorkspace.country || '—'} · ID <code>{currentWorkspace.id.slice(0, 8)}</code>
                                    </div>
                                </div>
                                {canManageWorkspace && (
                                    <Button size="sm" variant="outline" onClick={startEdit} data-testid="workspace-edit-btn">
                                        <Pencil size={14} className="mr-1.5" /> Renommer
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                                <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEdit(); }}
                                    className="max-w-md font-heading text-xl" autoFocus data-testid="workspace-name-input" />
                                <Button size="sm" onClick={saveName} disabled={savingName} className="bg-primary hover:bg-primary-hover" data-testid="workspace-save-btn">
                                    {savingName ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" /> Enregistrer</>}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelEdit}><X size={14} /></Button>
                            </div>
                        )}
                    </div>
                    {canManageWorkspace && !editing && (
                        <Button variant="destructive" size="sm" onClick={() => { setConfirmName(''); setDeleteOpen(true); }}
                            data-testid="workspace-delete-btn">
                            <Trash2 size={14} className="mr-1.5" /> Supprimer l'espace
                        </Button>
                    )}
                </div>
                {!canManageWorkspace && (
                    <p className="text-xs text-muted-foreground mt-3">Vous devez être administrateur de l'espace pour modifier ces paramètres.</p>
                )}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="card-soft overflow-hidden">
                        <div className="px-6 py-4 border-b border-border"><h2 className="font-heading text-2xl">{t('admin.members')}</h2></div>
                        <table className="w-full">
                            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <tr><th className="px-6 py-3">Membre</th><th className="px-6 py-3">{t('admin.role')}</th><th className="px-6 py-3">{t('admin.is_admin')}</th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {members.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-foreground/60">Aucun membre.</td></tr>}
                                {members.map((m) => (
                                    <tr key={m.user_id}>
                                        <td className="px-6 py-3"><div className="font-medium">{m.profiles?.full_name || '—'}</div><div className="text-xs text-muted-foreground">{m.profiles?.email}</div></td>
                                        <td className="px-6 py-3"><span className="phase-badge bg-secondary-light text-secondary">{m.role}</span></td>
                                        <td className="px-6 py-3">{m.is_admin ? t('common.yes') : t('common.no')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {invitations.length > 0 && (
                        <div className="card-soft overflow-hidden">
                            <div className="px-6 py-4 border-b border-border"><h2 className="font-heading text-2xl">Invitations</h2></div>
                            <table className="w-full">
                                <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr><th className="px-6 py-3">Email</th><th className="px-6 py-3">Rôle</th><th className="px-6 py-3">Statut</th><th className="px-6 py-3"></th></tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {invitations.map(inv => (
                                        <tr key={inv.id} data-testid={`invitation-row-${inv.id}`}>
                                            <td className="px-6 py-3 font-medium">{inv.email}</td>
                                            <td className="px-6 py-3 text-foreground/80">{inv.role}{inv.is_admin && ' · admin'}</td>
                                            <td className="px-6 py-3 text-sm">{inv.accepted_at ? <span className="text-secondary">Acceptée</span> : <span className="text-muted-foreground">En attente</span>}</td>
                                            <td className="px-6 py-3 text-right">
                                                {!inv.accepted_at && <Button size="sm" variant="ghost" onClick={() => copyLink(inv.token)} data-testid={`invitation-copy-${inv.id}`}><Copy size={12} className="mr-1" /> Lien</Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="space-y-8 h-fit">
                <div className="card-soft p-6">
                    <h2 className="font-heading text-2xl mb-2">Abonnement & facturation</h2>
                    <p className="text-sm text-foreground/65 mb-4">
                        Gérez votre carte bancaire, téléchargez vos factures ou résiliez votre abonnement via le portail sécurisé Stripe.
                    </p>
                    <Button variant="outline" className="w-full" data-testid="billing-portal-btn"
                        onClick={async () => {
                            try {
                                const r = await axios.post(`${API}/billing/stripe/portal`,
                                    { origin_url: window.location.origin },
                                    { headers: { Authorization: `Bearer ${session.access_token}` } });
                                if (r.data?.url) window.location.href = r.data.url;
                            } catch (e) {
                                toast.error(e.response?.data?.detail || e.message);
                            }
                        }}>
                        Ouvrir le portail de facturation Stripe
                    </Button>
                </div>
                <div className="card-soft p-6 h-fit">
                    <h2 className="font-heading text-2xl mb-5">{t('admin.invite')}</h2>
                    <form onSubmit={invite} className="space-y-4" data-testid="invite-form">
                        <div className="space-y-2"><Label>{t('admin.email')}</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></div>
                        <div className="space-y-2"><Label>{t('admin.role')}</Label>
                            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="invite-role">
                                {ROLES.map(r => <option key={r}>{r}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} className="rounded border-border" data-testid="invite-admin-flag" /> {t('admin.is_admin')}</label>
                        {feedback && (
                            <div className={`text-sm px-3 py-2 rounded-md ${feedback.type === 'ok' ? 'bg-secondary-light text-secondary' : feedback.type === 'warn' ? 'bg-primary-light text-primary' : 'bg-destructive/10 text-destructive'}`} data-testid="invite-feedback">
                                {feedback.message}
                            </div>
                        )}
                        {pendingLink && (
                            <div className="text-xs font-mono break-all bg-surface-muted p-2 rounded border border-border" data-testid="invite-link">
                                {pendingLink}
                            </div>
                        )}
                        <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary-hover" data-testid="invite-submit">
                            {busy ? <Loader2 className="animate-spin" size={14} /> : t('admin.add')}
                        </Button>
                    </form>
                </div>
                </div>
            </div>

            {/* Delete workspace confirmation dialog */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-destructive">Supprimer l'espace</DialogTitle>
                        <DialogDescription>
                            Cette action est <strong>irréversible</strong>. Tous les projets, documents, dépenses, réunions et
                            membres de cet espace seront définitivement supprimés.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm">
                            Pour confirmer, saisissez exactement le nom de l'espace : <strong>{currentWorkspace.name}</strong>
                        </p>
                        <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                            placeholder={currentWorkspace.name} autoFocus data-testid="workspace-delete-confirm-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={confirmDelete}
                            disabled={busy || normalize(confirmName) !== normalize(currentWorkspace.name)}
                            data-testid="workspace-delete-confirm-btn">
                            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                            Supprimer définitivement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
